import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

const POPUP_WIDTH = 360;

interface Props {
  /** Bounding rect of the button that opened the popup; null = closed. */
  anchorRect: DOMRect | null;
  childName: string;
  /** Scheduled session times — used both as defaults for the inputs and
   *  as the reference point for the variance hint next to each label. */
  scheduledStart: string;
  scheduledEnd: string;
  /** Pre-fill values for the time + comment inputs. Use the existing
   *  attendance row when completing a partial sign-in; fall back to
   *  scheduled times for a brand-new record. */
  defaultArrival: string;
  defaultDeparture: string;
  defaultArrivalComment?: string;
  defaultDepartureComment?: string;
  /** Tweaks the title copy when the user is finishing a partial sign-in
   *  rather than recording a session from scratch. */
  isCompletingSignIn?: boolean;
  onConfirm: (
    arrival: string,
    departure: string,
    arrivalComment: string | null,
    departureComment: string | null,
  ) => void;
  onCancel: () => void;
}

// HH:MM → minutes since midnight. Mirrors the helper inside
// AttendanceDetailPanel.tsx so the variance label here reads identically.
function timeToMins(t: string): number {
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
}

// Any non-zero variance from the scheduled time surfaces the comment
// textarea — even one minute off prompts the user to capture a reason.
// (We deliberately don't apply the dashboard's ±5 min on-time grace
// here: a custom-entered time means the user picked it on purpose, so
// even small deviations are worth annotating.)
function isOffTime(scheduled: string, actual: string): boolean {
  return timeToMins(actual) !== timeToMins(scheduled);
}

function variance(scheduled: string, actual: string | null): string | null {
  if (!actual) return null;
  const delta = timeToMins(actual) - timeToMins(scheduled);
  if (delta === 0) return null;
  const sign = delta > 0 ? '+' : '';
  const adj = delta > 0 ? 'late' : 'early';
  return `${sign}${delta} min ${adj}`;
}

// Anchored popup that lets the user pick custom arrival + departure times
// when marking a backlogged session attended. Time controls + variance
// labels + comment textareas are intentionally identical to the Attendance
// page's AttendanceDetailPanel so the two surfaces feel like the same
// editor (same component, same defaults, same idioms).
const AttendedTimesModal: React.FC<Props> = ({
  anchorRect,
  childName,
  scheduledStart,
  scheduledEnd,
  defaultArrival,
  defaultDeparture,
  defaultArrivalComment,
  defaultDepartureComment,
  isCompletingSignIn,
  onConfirm,
  onCancel,
}) => {
  const [arrival, setArrival] = useState(defaultArrival);
  const [departure, setDeparture] = useState(defaultDeparture);
  const [arrivalComment, setArrivalComment] = useState(defaultArrivalComment ?? '');
  const [departureComment, setDepartureComment] = useState(defaultDepartureComment ?? '');

  // Reset to defaults whenever the popup is dismissed or the defaults change
  // (e.g. the user opens the modal on a different row).
  useEffect(() => {
    if (!anchorRect) {
      setArrival(defaultArrival);
      setDeparture(defaultDeparture);
      setArrivalComment(defaultArrivalComment ?? '');
      setDepartureComment(defaultDepartureComment ?? '');
    }
  }, [anchorRect, defaultArrival, defaultDeparture, defaultArrivalComment, defaultDepartureComment]);

  // Vertical placement: prefer below the trigger, flip above when the popup
  // would run off the bottom of the viewport (small screens / low button),
  // clamp on-screen as a fallback. Measured after render so the real height
  // is used; useLayoutEffect avoids a visible jump. (Kept above the early
  // return so the hook order stays stable.)
  const dialogRef = useRef<HTMLDivElement>(null);
  const [top, setTop] = useState(0);

  useLayoutEffect(() => {
    if (!anchorRect) return;
    const h = dialogRef.current?.offsetHeight ?? 0;
    const margin = 8;
    const below = anchorRect.bottom + margin;
    const above = anchorRect.top - h - margin;
    let next: number;
    if (below + h <= window.innerHeight - margin) {
      next = below;                                              // fits below
    } else if (above >= margin) {
      next = above;                                              // flip above
    } else {
      next = Math.max(margin, window.innerHeight - h - margin);  // clamp on-screen
    }
    setTop(next);
  }, [anchorRect, arrival, departure, arrivalComment, departureComment]);

  if (!anchorRect) return null;

  // HH:MM string compare is a valid lexical compare so long as both values
  // are in the same day, which they are by construction here.
  const bothFilled = !!arrival && !!departure;
  const orderValid = bothFilled && arrival < departure;
  const valid = bothFilled && orderValid;

  const arrivalVariance = variance(scheduledStart, arrival);
  const departureVariance = variance(scheduledEnd, departure);
  const arrivalIsOffTime = !!arrival && isOffTime(scheduledStart, arrival);
  const departureIsOffTime = !!departure && isOffTime(scheduledEnd, departure);

  const handleConfirm = () => {
    if (!valid) return;
    // Only persist a comment for the side that's actually early/late.
    // If the user typed a note then dialled the time back to on-time, the
    // comment was about an off-time event that no longer applies, so it's
    // dropped rather than saved against an on-time entry.
    onConfirm(
      arrival,
      departure,
      arrivalIsOffTime ? (arrivalComment.trim() || null) : null,
      departureIsOffTime ? (departureComment.trim() || null) : null,
    );
  };

  // Horizontal: center on the trigger, clamped 8px inside the viewport.
  // (Vertical `top` is computed above via useLayoutEffect.)
  const centerX = anchorRect.left + anchorRect.width / 2;
  const left = Math.min(
    Math.max(8, centerX - POPUP_WIDTH / 2),
    window.innerWidth - POPUP_WIDTH - 8,
  );

  // Same fresh green as the .dash-now__pill / Attended CTA so the modal
  // reads as the action's accompanying detail panel.
  const accent = '#7cb342';
  const tintBg = '#eef7e2';

  // Match the AttendanceDetailPanel's label style verbatim so the two
  // editors feel like the same component.
  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 600,
    marginBottom: 4,
    color: '#444',
  };

  const title = isCompletingSignIn
    ? `Complete ${childName}'s attendance`
    : `Mark ${childName} attended`;

  return createPortal(
    <>
      <div
        onMouseDown={onCancel}
        style={{
          position: 'fixed', inset: 0, zIndex: 9998,
          background: 'transparent',
        }}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="att-times-title"
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: 'fixed',
          top, left,
          width: POPUP_WIDTH,
          zIndex: 9999,
          background: '#ffffff',
          border: `1px solid ${accent}`,
          borderTop: `4px solid ${accent}`,
          borderRadius: 10,
          boxShadow: '0 10px 28px -6px rgba(0,0,0,0.22), 0 4px 8px rgba(0,0,0,0.08)',
          overflow: 'hidden',
          maxHeight: 'calc(100vh - 32px)',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '12px 16px',
            background: tintBg,
            borderBottom: `1px solid ${accent}33`,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              width: 28,
              height: 28,
              borderRadius: '50%',
              background: accent,
              color: '#fff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              flexShrink: 0,
            }}
          >
            <i className="fas fa-check-double" />
          </span>
          <h6
            id="att-times-title"
            style={{
              fontWeight: 600,
              margin: 0,
              fontSize: '0.92rem',
              color: '#2a3528',
            }}
          >
            {title}
          </h6>
        </div>

        <div style={{ padding: '14px 16px', overflowY: 'auto' }}>
          <label style={labelStyle}>
            Arrival
            {arrivalVariance && (
              <span style={{ marginLeft: 8, color: '#a06400', fontWeight: 500 }}>
                ({arrivalVariance})
              </span>
            )}
          </label>
          <input
            type="time"
            value={arrival}
            onChange={(e) => setArrival(e.target.value)}
            className="form-control form-control-sm"
            style={{ marginBottom: arrivalIsOffTime ? 8 : 14 }}
            autoFocus
            required
          />
          {arrivalIsOffTime && (
            <textarea
              value={arrivalComment}
              onChange={(e) => setArrivalComment(e.target.value)}
              placeholder="e.g. taxi delayed"
              maxLength={500}
              rows={2}
              className="form-control form-control-sm"
              style={{ marginBottom: 14, resize: 'vertical' }}
            />
          )}

          <label style={labelStyle}>
            Departure
            {departureVariance && (
              <span style={{ marginLeft: 8, color: '#a06400', fontWeight: 500 }}>
                ({departureVariance})
              </span>
            )}
          </label>
          <input
            type="time"
            value={departure}
            onChange={(e) => setDeparture(e.target.value)}
            className="form-control form-control-sm"
            style={{ marginBottom: departureIsOffTime ? 8 : 4 }}
            required
          />
          {departureIsOffTime && (
            <textarea
              value={departureComment}
              onChange={(e) => setDepartureComment(e.target.value)}
              placeholder="e.g. left early for appointment"
              maxLength={500}
              rows={2}
              className="form-control form-control-sm"
              style={{ marginBottom: 4, resize: 'vertical' }}
            />
          )}

          {bothFilled && !orderValid && (
            <div
              style={{
                fontSize: '0.74rem',
                color: '#b8302f',
                marginTop: 8,
                marginBottom: 4,
              }}
            >
              Departure must be after arrival.
            </div>
          )}

          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 14 }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '7px 14px',
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.16)',
                borderRadius: 6,
                color: '#4a5b48',
                fontWeight: 500,
                fontSize: '0.85rem',
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={!valid}
              style={{
                padding: '7px 16px',
                background: accent,
                border: 'none',
                borderRadius: 6,
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.85rem',
                cursor: valid ? 'pointer' : 'not-allowed',
                opacity: valid ? 1 : 0.5,
                boxShadow: valid ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
              }}
            >
              Save attendance
            </button>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default AttendedTimesModal;
